// Wheatish skin-tone presets — 5 shades from light cream to soft tan.
// Applied at runtime by multiplying every skin material's
// baseColorFactor by the picked tint. The baked Daz textures stay
// neutral; the tint shifts overall hue/saturation without losing
// pore / shading detail.
//
// Used by the Jockey inline customizer (1820 / 1841 / MI16 / FE93) —
// a row of swatches in ModelViewerContainer toggles between them.

const SKIN_NAME_PREFIXES = [
  "Body", "Head", "Arm", "Leg", "Fingernails", "Toenails",
];

const srgbToLinear = (v) => {
  v = v / 255;
  return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
};

const hexToLinearRgba = (hex) => {
  const h = hex.replace("#", "");
  return [
    srgbToLinear(parseInt(h.slice(0, 2), 16)),
    srgbToLinear(parseInt(h.slice(2, 4), 16)),
    srgbToLinear(parseInt(h.slice(4, 6), 16)),
    1.0,
  ];
};

export const WHEATISH_TONES = [
  { id: "w1", label: "Light Cream", hex: "#F5E4D2" },
  { id: "w2", label: "Soft Beige",  hex: "#EFD7BD" },
  { id: "w3", label: "Wheatish",    hex: "#E8C9A6" },
  { id: "w4", label: "Warm Beige",  hex: "#E0BB8E" },
  { id: "w5", label: "Soft Tan",    hex: "#D6AB78" },
].map((t) => ({ ...t, linear: hexToLinearRgba(t.hex) }));

// W3 Wheatish — middle of the spectrum, closest to a neutral baseline.
export const DEFAULT_TONE_INDEX = 2;

const isSkinMaterial = (name) =>
  SKIN_NAME_PREFIXES.some((p) => (name || "").startsWith(p));

// Patch a model-viewer's loaded model in place. Iterates model.materials,
// calls setBaseColorFactor on every skin material with the linear RGBA
// of the picked tone. Idempotent. Returns the count of patched materials
// (or 0 if the model isn't loaded yet).
export const applyWheatishTone = (modelViewer, toneIdx) => {
  if (!modelViewer?.model?.materials) return 0;
  const tone = WHEATISH_TONES[toneIdx];
  if (!tone) return 0;
  let n = 0;
  for (const mat of modelViewer.model.materials) {
    if (!isSkinMaterial(mat?.name)) continue;
    const pbr = mat?.pbrMetallicRoughness;
    if (!pbr?.setBaseColorFactor) continue;
    pbr.setBaseColorFactor(tone.linear);
    n += 1;
  }
  return n;
};

// localStorage key — selection survives iframe reloads on product
// switch + page refresh.
const TONE_STORAGE_KEY = "dopplr_jockey_skin_tone_idx";

export const loadStoredTone = () => {
  try {
    const v = typeof localStorage !== "undefined"
      ? localStorage.getItem(TONE_STORAGE_KEY) : null;
    if (v == null) return DEFAULT_TONE_INDEX;
    const i = parseInt(v, 10);
    return Number.isFinite(i) && i >= 0 && i < WHEATISH_TONES.length
      ? i : DEFAULT_TONE_INDEX;
  } catch { return DEFAULT_TONE_INDEX; }
};

export const saveStoredTone = (idx) => {
  try { localStorage.setItem(TONE_STORAGE_KEY, String(idx)); } catch {
    /* private mode / quota: ignore */
  }
};
